import type { GetHistoryTravelAmountRequest } from '@/models/api/get-history-travel-amount-request'
import {
  getThresholdCourtesySchema,
  type GetThresholdCourtesy
} from '@/models/api/get-threshold-courtesy'
import type { HistoryTravelAmount } from '@/models/api/history-travel-amount'
import { historyTravelAmountSchema } from '@/models/api/history-travel-amount'
import type { InvoicingVolumeInformation } from '@/models/api/invoicing-volume-information'
import { invoicingVolumeInformationSchema } from '@/models/api/invoicing-volume-information'
import type { PointAndCourtesy } from '@/models/api/point-and-courtesy'
import { pointAndCourtesySchema } from '@/models/api/point-and-courtesy'
import {
  travelAmountStatisticsGetFlownAmountSchema,
  type TravelAmountStatisticsGetFlownAmount
} from '@/models/api/travel-amount-statistics-get-flown-amount'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class TravelAmountStatisticsApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 取得點數及可兌換禮遇數量
   * https://hackmd.io/ny-4-p8lQjS6kfdiiAROFA
   */
  @apiValidator(pointAndCourtesySchema)
  getPointAndCourtesy(
    bizId: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<PointAndCourtesy>> {
    return this.api.get(
      `TravelAmountStatistics/GetPointAndCourtesy/${bizId}`,
      config
    )
  }

  /**
   * API 取得本次合約期間搭機總金額
   * https://hackmd.io/PwJZPqKiSomP_CUqX8IwEw
   */
  @apiValidator(travelAmountStatisticsGetFlownAmountSchema)
  getFlownAmount(
    bizId: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<TravelAmountStatisticsGetFlownAmount>> {
    return this.api.get(
      `TravelAmountStatistics/GetFlownAmount/${bizId}`,
      config
    )
  }

  /**
   * API 取得本次合約期間開票量方案資料列表
   * https://hackmd.io/zLucj1-HRR-6Z338RMvA1g
   */
  @apiValidator(invoicingVolumeInformationSchema.array())
  getInvoicingVolumeInformationList(
    bizId: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<InvoicingVolumeInformation[]>> {
    return this.api.get(
      `TravelAmountStatistics/GetInvoicingVolumeInformation/${bizId}`,
      config
    )
  }

  /**
   * API 查詢差旅金額其他(歷史)區間列表
   * https://hackmd.io/-8fPvsYORnmQZBBvZ_q0zQ
   */
  @apiValidator(historyTravelAmountSchema.array())
  getHistoryTravelAmountList(
    query: GetHistoryTravelAmountRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<HistoryTravelAmount[]>> {
    return this.api.post(
      `TravelAmountStatistics/GetHistoryTravelAmount`,
      query,
      config
    )
  }

  /**
   * API 取得各檻次禮遇介紹
   * https://hackmd.io/J0N1rNGjRgWk0tG1ArMK_A
   */
  @apiValidator(getThresholdCourtesySchema.array())
  getThresholdCourtesyList(
    bizId: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<GetThresholdCourtesy[]>> {
    return this.api.get(
      `TravelAmountStatistics/GetThresholdCourtesy/${bizId}`,
      config
    )
  }
}
